/**
 * Agents hooks (v1.1-D1) — full real-data binding for the /agents UI.
 * Replaces every MOCK_AGENTS / MOCK_EXECUTIONS / MOCK_EXECUTION_STEPS
 * read.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './api-client';
import type {
  MockAgent,
  AgentStatus,
  AgentOwnership,
  AgentAutonomy,
  AgentVisibility,
  AgentCategory,
  AgentTrigger,
  AgentExecution,
} from './mock-agents-data';

interface ServerAgent {
  id: string;
  name: string;
  description: string | null;
  icon: string;
  ownership: string;
  visibility: string;
  trustLevel: number;
  status: string;
  environment: string | null;
  category: string | null;
  tags: unknown;
  tools: unknown;
  connectedSystems: unknown;
  autonomyLevel: string | null;
  triggerModes: unknown;
  usageCount: number;
  successRate: number | null;
  createdBy: string;
  createdByName?: string | null;
  ownerTeam: string | null;
  createdAt: string;
  systemPrompt: string | null;
  persona: string | null;
  skills: unknown;
  knowledgeScope: unknown;
  version: number;
  isBuiltin: boolean;
  slug: string | null;
  approvalPolicy: string | null;
  defaultBrand: string | null;
  lastUsedAt: string | null;
  updatedAt: string;
}

type AgentLibraryRefKind =
  | 'folder'
  | 'article'
  | 'tag'
  | 'document'
  | 'document_folder'
  | 'source';

interface AgentLibraryRef {
  kind: AgentLibraryRefKind;
  id: string;
}

interface AgentKnowledgeScope {
  libraryRefs?: AgentLibraryRef[];
  knowledgeSources?: string[];
  [key: string]: unknown;
}

const ICON_COLOR_BY_CATEGORY: Record<string, string> = {
  operations: '#1890FF',
  maintenance: '#FA8C16',
  safety: '#F5222D',
  compliance: '#13C2C2',
  documentation: '#722ED1',
  analytics: '#52C41A',
  custom: '#1890FF',
};

/**
 * Valid `AgentCategory` keys the UI knows how to render. Kept in sync with
 * mock-agents-data.ts. The server can (and does — see #1107) emit values
 * outside this set (`general`, `studio`, ...), so the adapter must
 * normalise unknowns to `custom` instead of letting them flow through.
 */
const KNOWN_AGENT_CATEGORIES = new Set<AgentCategory>([
  'operations',
  'maintenance',
  'safety',
  'compliance',
  'documentation',
  'analytics',
  'custom',
]);

function pickStrArr(v: unknown): string[] {
  return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
}

function pickKnowledgeSources(scope: unknown): string[] {
  const obj = scope && typeof scope === 'object'
    ? scope as AgentKnowledgeScope
    : null;
  if (!obj) return [];
  if (Array.isArray(obj.libraryRefs)) {
    const fromRefs = obj.libraryRefs
      .filter((ref) => typeof ref.kind === 'string' && typeof ref.id === 'string')
      .map((ref) => `${ref.kind}:${ref.id}`);
    return [...fromRefs, ...pickStrArr(obj.knowledgeSources)];
  }
  return Object.keys(obj);
}

export function adaptAgent(a: ServerAgent): MockAgent {
  // #1107 — normalise unknown categories to `custom`. The pre-fix cast
  // `(a.category as AgentCategory) ?? 'custom'` only handled null/undefined;
  // a string like `general` flowed through and crashed every consumer that
  // did `AGENT_CATEGORY_LABELS[agent.category].color`.
  const rawCat = (a.category ?? '').trim();
  const cat: AgentCategory = KNOWN_AGENT_CATEGORIES.has(rawCat as AgentCategory)
    ? (rawCat as AgentCategory)
    : 'custom';
  return {
    id: a.id,
    name: a.name,
    description: a.description ?? '',
    icon: a.icon,
    iconColor: ICON_COLOR_BY_CATEGORY[cat] ?? '#1890FF',
    category: cat,
    status: (a.status as AgentStatus) ?? 'draft',
    isBuiltin: a.isBuiltin,
    ownership: (a.ownership as AgentOwnership) ?? 'personal',
    modelTask: 'chat',
    modelId: a.defaultBrand ?? 'argus-pro',
    systemPrompt: a.systemPrompt ?? '',
    maxSteps: 12,
    autonomyLevel: (a.autonomyLevel as AgentAutonomy) ?? 'read_only',
    triggerModes: pickStrArr(a.triggerModes) as AgentTrigger[],
    tools: pickStrArr(a.tools),
    skills: pickStrArr(a.skills),
    knowledgeSources: pickKnowledgeSources(a.knowledgeScope),
    guardrailProfile: 'standard',
    tags: pickStrArr(a.tags),
    visibility: (a.visibility as AgentVisibility) ?? 'private',
    metrics: {
      totalQueries: a.usageCount ?? 0,
      avgResponseMs: 0,
      successRate: (a.successRate ?? 0) / 100,
      satisfactionScore: 0,
    },
    createdBy: a.createdBy,
    createdByName: a.createdByName ?? null,
    // misc-cluster-aaa #580 (AGENTS-HUB-005): hand the bare version
    // number to the UI. Every consumer renders `v{agent.version}`; the
    // pre-fix `v${a.version}` here produced the `vv274` double-prefix
    // bug reported on /agents and on the agent detail page.
    version: String(a.version),
    createdAt: a.createdAt,
    updatedAt: a.updatedAt,
  };
}

export function useAgents(view: 'all' | 'my' | 'shared' | 'company' = 'all') {
  return useQuery<{ data: ServerAgent[]; total: number }, Error, MockAgent[]>({
    queryKey: ['custom-agents', view],
    queryFn: () =>
      apiClient.get<{ data: ServerAgent[]; total: number }>(
        `/custom-agents?view=${view}`,
      ),
    select: (r) => r.data.map(adaptAgent),
  });
}

/**
 * UUID v4-ish shape check. The agent detail route param is either a real
 * agents.id UUID (custom/tenant agents) OR a system-agent slug like
 * `system.ask_argus` / `ask-argus` (Insights bug #5 — the founding system
 * agent is keyed by slug in query_metrics, not by UUID). We branch the
 * fetch on the param shape so a slug resolves via the by-slug endpoint
 * (org-scoped tenant slug first, then the org_id-IS-NULL system row).
 */
const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function useAgent(id: string | undefined) {
  return useQuery<{ data: ServerAgent } | ServerAgent, Error, MockAgent | null>({
    queryKey: ['custom-agents', 'detail', id],
    enabled: !!id,
    queryFn: () => {
      const isUuid = !!id && UUID_RE.test(id);
      const path = isUuid
        ? `/custom-agents/${id}`
        : `/custom-agents/by-slug/${encodeURIComponent(id ?? '')}`;
      return apiClient.get<ServerAgent | { data: ServerAgent }>(path);
    },
    select: (r) => {
      const row = (r as { data?: ServerAgent }).data ?? (r as ServerAgent);
      return row ? adaptAgent(row) : null;
    },
  });
}

// ─── Mutations ────────────────────────────────────────────────────

/**
 * POST /custom-agents — create a new custom agent.
 *
 * Body shape mirrors the Zod body declared at
 * packages/api/src/routes/v1/custom-agents.ts (POST /). Built-in
 * agents cannot be created via this path — they are seeded.
 */
export interface CreateAgentInput {
  name: string;
  description?: string;
  icon?: string;
  systemPrompt: string;
  persona?: string;
  ownership?: 'personal' | 'team' | 'company';
  ownerTeam?: string;
  tools?: string[];
  /** phase4/catalog-reference-picker — multi-skill composition. */
  skills?: string[];
  knowledgeScope?: AgentKnowledgeScope;
  tags?: string[];
  category?: string;
  triggerModes?: string[];
  autonomyLevel?:
    | 'read_only'
    | 'draft_only'
    | 'approval_required'
    | 'auto_execute';
  approvalPolicy?: 'auto' | 'require_approval';
  /**
   * phase4/catalog-reference-picker — multi-model selection. Array of
   * argus_models.id UUIDs. The backend persists into agents.bases via
   * the post-bU8d view+trigger. When undefined or empty, the agent has
   * no models assigned and the runtime auto-routes.
   *
   * Sysadmin/internal callers that have UUID-surface access use this.
   */
  modelIds?: string[];
  /**
   * phase4/catalog-reference-picker (C1a audit fix) — tenant-facing
   * stable brand_id strings (e.g. 'argus-pro'). The backend resolves
   * each brand_id to an argus_models.id UUID via the tenant's
   * argus_model_assignments. Tenants MUST use this accessor because
   * argus_models.id is redacted from every tenant-facing endpoint per
   * invariant I-M6.7 — the tenant has no way to see the underlying
   * UUID. Brand_ids the tenant has not been assigned are silently
   * dropped server-side.
   */
  modelBrandIds?: string[];
}

export function useCreateAgent() {
  const qc = useQueryClient();
  return useMutation<ServerAgent, Error, CreateAgentInput>({
    mutationFn: (body) => apiClient.post<ServerAgent>('/custom-agents', body),
    onSuccess: () => {
      // Refresh every list view + every detail key so the new agent
      // appears in /agents immediately (bug #98).
      qc.invalidateQueries({ queryKey: ['custom-agents'] });
    },
  });
}

/**
 * PATCH /custom-agents/:id — update a custom agent.
 *
 * fix-wave / agents-approvals-cluster #1109 — `systemPrompt` is now wired
 * through. Pre-fix the backend Zod body schema silently stripped it and
 * the PATCH returned 200 with the prompt unchanged, which is exactly
 * the silent-non-persistence risk #1109 reported. autonomyLevel /
 * guardrailProfile / tools are still pending wiring.
 */
export interface UpdateAgentInput {
  name?: string;
  description?: string;
  status?: 'draft' | 'staging' | 'active' | 'paused' | 'archived';
  tags?: string[];
  category?: string;
  systemPrompt?: string;
  knowledgeScope?: AgentKnowledgeScope;
  // Platform audit 2026-06-12: now round-trips through PATCH (backend Zod accepts
  // it + the custom_agents view trigger persists it — proven on ai1).
  autonomyLevel?: 'read_only' | 'assisted' | 'draft_only' | 'approval_required' | 'auto_execute';
  approvalPolicy?: 'auto' | 'require_approval';
}

export function useUpdateAgent() {
  const qc = useQueryClient();
  return useMutation<
    ServerAgent,
    Error,
    { id: string; body: UpdateAgentInput }
  >({
    mutationFn: ({ id, body }) =>
      apiClient.patch<ServerAgent>(`/custom-agents/${id}`, body),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: ['custom-agents'] });
      qc.invalidateQueries({ queryKey: ['custom-agents', 'detail', id] });
    },
  });
}

// ─── Cluster C-STUB-TOAST — Delete custom agent ─────────────────────
//
// Cluster C-STUB-TOAST (Pod-S, 2026-05-15) — wires the agent detail
// page's Danger-Zone "Delete Agent" button to the real backend.
// Pre-fix the button only fired `toast.success(\`${agent.name} deleted\`)`
// and navigated to /agents, leaving the row in the DB. The endpoint
// already exists at packages/api/src/routes/v1/custom-agents.ts:614
// (DELETE /custom-agents/:id; tenant-scoped soft-ish delete).
//
// Per Phase 2 resolved decision #3 (founder-locked 2026-05-14) a stub
// toast on a destructive action is a P0 defect.
export function useDeleteAgent() {
  const qc = useQueryClient();
  return useMutation<{ success: boolean }, Error, { id: string }>({
    mutationFn: ({ id }) =>
      apiClient.delete<{ success: boolean }>(`/custom-agents/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['custom-agents'] });
    },
  });
}

// ─── Cluster C-STUB-TOAST follow-up (#1509) — Clone custom agent ──────
//
// Wires the Hub table's row "Clone" action to the real server-side
// clone endpoint (POST /custom-agents/:id/clone, custom-agents.ts:1076).
// Pre-fix the Hub handler pushed a throwaway local object + toast and
// never persisted — refreshing made the "clone" vanish. The endpoint
// copies all config (incl. built-ins) into a private 'draft' personal
// agent and returns it.
export function useCloneAgent() {
  const qc = useQueryClient();
  return useMutation<ServerAgent, Error, { id: string }>({
    mutationFn: ({ id }) =>
      apiClient.post<ServerAgent>(`/custom-agents/${id}/clone`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['custom-agents'] });
    },
  });
}

// ─── Agent executions (built from agent_invocations) ──────────────

interface ServerInvocation {
  id: string;
  agentId: string;
  agentSlug: string;
  status: string;
  invokedAt: string;
  promptTokens: number | null;
  completionTokens: number | null;
  latencyMs: number | null;
}

export function useAgentExecutions(agentId: string | undefined) {
  return useQuery<
    { invocations: ServerInvocation[] },
    Error,
    AgentExecution[]
  >({
    queryKey: ['custom-agents', 'executions', agentId],
    enabled: !!agentId,
    queryFn: () =>
      apiClient.get<{ invocations: ServerInvocation[] }>(
        `/custom-agents/${agentId}/invocations`,
      ),
    select: (r) =>
      r.invocations.map((inv) => ({
        id: inv.id,
        status:
          inv.status === 'success'
            ? 'success'
            : inv.status === 'timeout'
            ? 'timeout'
            : 'error',
        query: inv.agentSlug || inv.agentId,
        durationMs: inv.latencyMs ?? 0,
        toolCalls: 0,
        timestamp: inv.invokedAt,
      })),
  });
}
